/* Atlas V3 Desktop — Overview (the market pulse home).
   Hero index chart (Atlas source = whole-tier atlas_index %; other sources =
   live chart-bars composites) ·
   key movers · The Brief (atlas_intel) · On the Wire (signals + news) ·
   Sectors rail · Pick of the Week. All wired to the same hooks the mobile
   Overview uses. openNews/openPick come from the router (mount the sheets). */
import { useMemo, useState } from 'react'
import { HeroChart } from '@/v2/v3/HeroChart'
import { SectorLines, sectorHeadline, toggleSector } from '@/v2/v3/SectorLines'
import { BriefSheet } from '@/v2/v3/BriefSheet'
import { StockLogo } from '@/v2/components/StockLogo'
import { Pct, Chip, intent, signalWriteup, signalToSheet, useTicker } from '@/v2/v3/lib'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useUniversePrices } from '@/v2/hooks/useUniversePrices'
import { useChartBars } from '@/v2/hooks/useChartBars'
import { usePortfolioTickers } from '@/v2/hooks/usePortfolioTickers'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useUserPref } from '@/v2/hooks/useUserPref'
import { useAtlasIntel, usePickOfWeek, useSignalsRecent, useNews, useAtlasIndex, useSectorIndex } from '@/v2/hooks/useSupabaseData'
import { Crossfade, SkelChart, SkelDisp, SkelText, SkelCircle, SkelEditorial, enterStyle } from '../qol'

const PULSE_TFS = [['1D', '1D'], ['1W', '5D'], ['1M', '1M'], ['6M', '6M'], ['1Y', '1Y']]
// Same vocabulary as the index chips wherever the data supports it — 1M/6M/1Y
// are the identical labels and windows — then extended with 5Y/MAX. It does NOT
// offer 1D/1W because `sector_index` stores ONE row per sector per trading day:
// 1D would be a single point and 1W five. A separate 1Y/3Y/5Y/MAX vocabulary
// was the wrong answer to that (Ben: "why wouldnt it just match the existing
// chart"); matching where it can and stopping where the data stops is honest.
// Third field is the log flag — past 1Y the sectors spread over orders of
// magnitude and a linear axis crushes all but the leader onto the baseline.
const SECTOR_TFS = [['1M', 30, false], ['6M', 182, false], ['1Y', 365, false], ['5Y', 1825, true], ['MAX', 0, true]]

/* First editorial paragraph of the intel body — mirrors mobile Overview.
   GARBAGE_INTEL catches the writer describing its own empty input ("empty TOP
   GAINERS list…"); never surface that — fall back to the clean default. */
const GARBAGE_INTEL = /sector pulse|top gainers|top losers|no (percentage|company description|headline|data)|not provided|provided in the|empty .{0,30}list/i
function firstParagraph(body) {
  if (!body) return null
  const line = body.split('\n').map((l) => l.trim())
    .find((l) => l && !/^#{1,3}\s/.test(l) && !/^[-*+]\s/.test(l) && !/^(?:---+|\*\*\*+)$/.test(l) && !/Atlas Intel/i.test(l))
  if (!line || GARBAGE_INTEL.test(line)) return null
  return line.replace(/[*_#]/g, '')
}
function relTime(iso) {
  if (!iso) return ''
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export function Overview({ openNews, openPick }) {
  const openTicker = useTicker()
  const [briefOpen, setBriefOpen] = useState(false)
  const { stocks } = useAtlasStocks()
  const { intel, loading: intelLoading } = useAtlasIntel()
  const { active: pick, stats, history, loading: pickLoading } = usePickOfWeek()
  const { events, loading: sigLoading } = useSignalsRecent(80)
  const { news, loading: newsLoading } = useNews()
  const { levels: idxLevels } = useAtlasIndex()

  // Prices over the whole active universe via the SHARED provider (one fetch
  // app-wide — the ribbon reads the same context). Movers/sectors read the
  // full pool; the hero chart's composite basket is the top-40 slice.
  const tickers = useMemo(() => stocks.map((s) => s.ticker), [stocks])
  const basket = useMemo(() => tickers.slice(0, 40), [tickers])
  const { prices, loading: pricesLoading } = useUniversePrices()

  // Hero source list — Atlas Index · Portfolio · Watchlists · Sectors
  // composites, mirroring mobile Pulse's chartSources.
  const { tickers: portfolioTickers, signedIn } = usePortfolioTickers()
  const { watchlists } = useWatchlists()
  const sectorMap = useMemo(() => {
    const m = {}
    for (const s of stocks) {
      if (!s.sector || !s.ticker) continue
      ;(m[s.sector] = m[s.sector] || []).push(s.ticker)
    }
    return m
  }, [stocks])
  const chartSources = useMemo(() => {
    const out = [{ key: 'atlas', label: 'Atlas Index', tickers: basket, group: 'index' }]
    // Overnight world indices — Tokyo/Seoul/Taipei trade while the US sleeps
    // (the upstream tape for the AI supply chain). Single-symbol "baskets"
    // through the same composite path; chart-bars maps these keys to Yahoo
    // index symbols (^N225/^KS11/^TWII). Mirrors mobile Pulse's chartSources —
    // desktop was missing them entirely until 2026-08-22 (audit finding F9).
    out.push({ key: 'idx:NIKKEI', label: 'Nikkei 225 · Tokyo', tickers: ['NIKKEI'], group: 'world' })
    out.push({ key: 'idx:KOSPI', label: 'KOSPI · Seoul', tickers: ['KOSPI'], group: 'world' })
    out.push({ key: 'idx:TAIEX', label: 'TAIEX · Taipei', tickers: ['TAIEX'], group: 'world' })
    if (signedIn && portfolioTickers.length) out.push({ key: 'portfolio', label: 'Portfolio', tickers: portfolioTickers, group: 'portfolio' })
    for (const w of (watchlists || [])) if (w.tickers && w.tickers.length) out.push({ key: 'wl:' + w.id, label: w.name, tickers: w.tickers, group: 'watchlist' })
    for (const name of Object.keys(sectorMap).sort()) out.push({ key: 'sec:' + name, label: name, tickers: sectorMap[name], group: 'sector' })
    return out
  }, [basket, signedIn, portfolioTickers, watchlists, sectorMap])

  // Movers — top 6 by |change1d| across the universe. Prices land in parallel
  // batches; ranking mid-fill reshuffles the cluster on every batch (cells
  // remount → re-fade → logos refetch). Hold the skeleton until the fill
  // completes, then settle ONCE. Post-load refresh ticks update in place.
  const movers = useMemo(() => {
    if (pricesLoading) return []
    return tickers
      .map((t) => ({ t, c: prices[t]?.change1d }))
      .filter((m) => typeof m.c === 'number')
      .sort((a, b) => Math.abs(b.c) - Math.abs(a.c))
      .slice(0, 6)
  }, [tickers, prices, pricesLoading])

  // Live whole-index move on the day = equal-weight avg of change1d across the
  // active (main-tier) universe — the intraday equivalent of the atlas_index
  // close. Drives the Atlas Index 1D headline. null until prices land.
  const liveIndexPct = useMemo(() => {
    let sum = 0, n = 0
    for (const s of stocks) {
      const c = prices[s.ticker]?.change1d
      if (typeof c === 'number') { sum += c; n++ }
    }
    return n ? sum / n : null
  }, [stocks, prices])

  // Sectors — net avg change1d per sector, ranked. Same settle-once gate as
  // movers: a partial fill re-ranks the rail on every price batch.
  const sectors = useMemo(() => {
    if (pricesLoading) return []
    const byId = {}
    for (const s of stocks) {
      const c = prices[s.ticker]?.change1d
      if (typeof c !== 'number' || !s.sector) continue
      ;(byId[s.sector] = byId[s.sector] || []).push(c)
    }
    return Object.entries(byId)
      .map(([n, arr]) => ({ n, c: arr.reduce((a, b) => a + b, 0) / arr.length }))
      .sort((a, b) => b.c - a.c)
      .slice(0, 6)
  }, [stocks, prices, pricesLoading])

  // atlas_intel.title is "Atlas Intel — {date}" — the date goes in the kicker,
  // NOT the headline. Headline = sentence 1 of the body; body = next 1-2.
  // Sentence split respects decimals ("+17.1%") — mirrors mobile Brief.
  const intelDoc = intel[0] || null
  const dateLabel = (intelDoc?.title?.match(/[—–-]\s*(.+)$/)?.[1] || '').trim()
    || (intelDoc?.date ? new Date(intelDoc.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : '')
  const para = firstParagraph(intelDoc?.body ?? intelDoc?.preview)
  const sentences = para ? para.split(/(?<=[.!?])\s+(?=[A-Z“"])/).map((s) => s.trim()).filter(Boolean) : []
  const briefKicker = dateLabel ? `The Brief · ${dateLabel}` : 'The Brief'
  const briefHead = sentences[0] || 'Atlas is reading today’s tape.'
  const briefBody = sentences.slice(1, 3).join(' ') ||
    'Today’s briefing posts after the 5:40 PM CT cron. Atlas is tracking AI-infrastructure, defense-tech, and power megatrends.'
  // The card shows headline + 1-2 sentences; the full briefing (intelDoc.body)
  // is already loaded — tap to read it. Only when there's a real briefing.
  const briefHasFull = !!para

  return (
    <>
      <Hero idxLevels={idxLevels} liveIndexPct={liveIndexPct} sources={chartSources} movers={movers} onOpenTicker={openTicker} />

      <div className="v3web-body">
        <div>
          <section className="v3web-module">
            <Crossfade loading={intelLoading && !intelDoc} skeleton={<SkelEditorial headline={34} />}>
              <div className="v3web-sechead">
                <span className="v3web-kicker">{briefKicker}</span>
              </div>
              <div onClick={briefHasFull ? () => setBriefOpen(true) : undefined} style={{ cursor: briefHasFull ? 'pointer' : 'default' }}>
                <h1 className="v3web-briefh">{briefHead}</h1>
                <p className="v3web-briefp">{briefBody}</p>
                {briefHasFull && (
                  <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', marginTop: 8 }}>READ THE FULL BRIEFING →</div>
                )}
              </div>
              {briefOpen && <BriefSheet desktop doc={intelDoc} dateLabel={dateLabel} onClose={() => setBriefOpen(false)} />}
              <div className="v3web-briefchips">
                {movers.slice(0, 3).map((m) => {
                  const up = m.c >= 0
                  return (
                    <div key={m.t} className="v3web-chip" onClick={() => openTicker(m.t)}>
                      <StockLogo ticker={m.t} size={16} />
                      <span className="s">{m.t}</span>
                      <span className="c" style={{ color: `hsl(var(${intent(up)}))` }}>{up ? '+' : ''}{m.c.toFixed(2)}%</span>
                    </div>
                  )
                })}
              </div>
            </Crossfade>
          </section>

          <WireTeaser events={events} news={news} loading={(sigLoading || newsLoading)} onOpenTicker={openTicker} onOpenNews={openNews} />
        </div>

        <div className="v3web-rail">
          <section style={{ marginBottom: 30 }}>
            <div className="v3web-sechead"><span className="v3web-kicker">Markets · Sectors</span></div>
            <Crossfade loading={sectors.length === 0 && pricesLoading} skeleton={
              <div>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 2px', borderTop: i ? '1px solid hsl(var(--studio-border) / 0.4)' : 'none' }}>
                    <SkelText w={16} h={9} /><SkelText w={130} h={12} /><SkelText w={44} h={11} style={{ marginLeft: 'auto' }} />
                  </div>
                ))}
              </div>
            }>
              <div>
                {sectors.map((s, i) => {
                  const up = s.c >= 0
                  return (
                    <div key={s.n} className="v3web-secrow" style={enterStyle(i)}>
                      <span className="rk">{String(i + 1).padStart(2, '0')}</span>
                      <span className="nm">{s.n}</span>
                      <span className="c" style={{ color: `hsl(var(${intent(up)}))` }}>{up ? '+' : ''}{s.c.toFixed(1)}%</span>
                    </div>
                  )
                })}
              </div>
            </Crossfade>
          </section>

          {pickLoading && !pick && (
            <>
              <div className="v3web-sechead" style={{ marginTop: 6 }}><span className="v3web-kicker">Pick of the Week</span></div>
              <div style={{ background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderTop: '2px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)', padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: 13 }}>
                <SkelText w={150} h={9} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <SkelCircle size={34} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><SkelText w={54} h={14} /><SkelText w={90} h={9} /></div>
                </div>
                <SkelText w="100%" h={14} />
                <SkelText w="76%" h={14} />
              </div>
            </>
          )}
          {pick && <PickCard pick={pick} stats={stats} history={history} onOpen={() => openPick(pick.ticker)} />}
        </div>
      </div>

      <div className="v3web-disclaimer">AI-assisted research based on real signals. Not financial advice. Always verify independently.</div>
    </>
  )
}

const GROUP_LABEL = { world: 'Overnight', portfolio: 'Yours', watchlist: 'Watchlists', sector: 'Sectors' }

function Hero({ idxLevels, liveIndexPct, sources, movers, onOpenTicker }) {
  const [tf, setTf] = useState('1D')
  // Hero mode: the single Atlas index line (default) or the sector comparison.
  // Lazy — the sector series is only fetched once SECTORS is switched on.
  const [mode, setMode] = useState('index')
  const [sectorTf, setSectorTf] = useState('1Y')
  const sectorCfg = SECTOR_TFS.find(([l]) => l === sectorTf) || SECTOR_TFS[0]
  const sectorDays = sectorCfg[1]
  const sectorLog = sectorCfg[2]
  const { series: sectorSeries, loading: sectorLoading } = useSectorIndex(mode === 'sectors', sectorDays)
  // Which sectors the comparison draws. Empty = all of them (the survey
  // default). Persisted per user, so "Memory vs Semi Equip" survives a reload
  // and follows you between phone and desktop — the pair you are watching this
  // week is exactly the kind of state that is infuriating to re-pick.
  const [sectorSel, setSectorSel] = useUserPref('hero_sector_selection', [])
  const toggleSel = (id) => setSectorSel(toggleSector(sectorSel, id))
  const resetSel = () => setSectorSel([])
  const sectorHead = sectorHeadline(sectorSeries, sectorSel)
  const [srcKey, setSrcKey] = useState('atlas')
  const [menuOpen, setMenuOpen] = useState(false)
  const [hover, setHover] = useState(null)
  const range = (PULSE_TFS.find(([l]) => l === tf) || PULSE_TFS[0])[1]
  const isAtlas = srcKey === 'atlas'
  // Atlas Index source, two modes:
  //  · 1D  = LIVE whole-index move on the day → headline = live equal-weight avg
  //    change1d across the main tier (liveIndexPct); the LINE is the live intraday
  //    composite (chart-bars), endpoint-anchored to liveIndexPct so it lands on
  //    the headline number. Real, moving, whole-index — not a daily 2-point line.
  //  · >1D = the daily atlas_index series (true index over time; real curves).
  // Non-atlas sources (Portfolio / Watchlists / Sectors) stay live composites.
  const atlasDaily = isAtlas && range !== '1D'
  const cur = useMemo(() => sources.find((s) => s.key === srcKey) || sources[0] || { key: 'atlas', label: 'Atlas Index', tickers: [] }, [sources, srcKey])
  // Composite fetch for non-atlas sources AND for the live 1D atlas line.
  const chartBasket = useMemo(() => (atlasDaily ? [] : (cur.tickers || []).slice(0, 40)), [cur, atlasDaily])
  const { series, loading: chartLoading } = useChartBars(chartBasket, range)
  const [livePulse] = useUserPref('live_pulse_enabled', true)
  // Whole-index window from atlas_index daily levels, normalized to % from the
  // window start. SPAN = trading days per timeframe. (Used for >1D atlas only.)
  const idxWindow = useMemo(() => {
    if (!atlasDaily || !idxLevels?.length) return null
    const SPAN = { '5D': 6, '1M': 22, '6M': 126, '1Y': 252 }[range] || idxLevels.length
    const win = idxLevels.slice(-SPAN)
    if (win.length < 2) return { points: [], pct: 0 }
    const base = win[0].level
    const points = win.map((l) => ({ t: new Date(l.date + 'T00:00:00').getTime(), value: (l.level / base - 1) * 100 }))
    return { points, pct: points[points.length - 1].value }
  }, [atlasDaily, idxLevels, range])
  const composLast = series.length ? series[series.length - 1].pct : 0
  // Live 1D atlas: shift the intraday composite so its endpoint equals the true
  // live whole-index % — keeps the intraday shape, lands the line on the headline.
  const atlasLive = isAtlas && range === '1D'
  const atlasDelta = atlasLive && liveIndexPct != null ? liveIndexPct - composLast : 0
  const composPoints = useMemo(() => series.map((p) => ({ t: p.t, value: p.pct + atlasDelta })), [series, atlasDelta])
  const points = atlasDaily ? (idxWindow?.points || []) : composPoints
  const lastPct = atlasDaily ? (idxWindow?.pct || 0) : (atlasLive ? (liveIndexPct ?? composLast) : composLast)
  const chartLoading2 = atlasDaily ? !(idxLevels && idxLevels.length) : chartLoading
  const intraday = !atlasDaily && (tf === '1D' || tf === '1W')
  const scrub = hover && hover.value != null
  const shownPct = scrub ? hover.value : lastPct
  const numColor = intent(shownPct >= 0)
  const lineColor = intent(lastPct >= 0)

  return (
    <section className="v3web-hero">
      <div className="v3web-herotop">
        <div>
          {/* source switcher — Atlas Index / Portfolio / Watchlists / Sectors */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setMenuOpen((v) => !v)} className="v3web-idxkicker" style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: srcKey === 'atlas' ? 'hsl(var(--studio-text-subtle))' : 'hsl(var(--studio-accent-soft))' }}>
              <span>{cur.label}</span>
              <span style={{ fontSize: 8, opacity: 0.7 }}>▾</span>
            </button>
            {menuOpen && (
              <>
                <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                <div className="atlas-scroll v3web-pop" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 8, zIndex: 41, minWidth: 230, maxHeight: 340, overflowY: 'auto', background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 8, padding: 4, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
                  {sources.map((s, i) => {
                    const on = s.key === srcKey
                    const head = GROUP_LABEL[s.group] && (i === 0 || sources[i - 1].group !== s.group) ? GROUP_LABEL[s.group] : null
                    return (
                      <div key={s.key}>
                        {head && <div style={{ padding: '8px 10px 3px', fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))' }}>{head.toUpperCase()}</div>}
                        <button onClick={() => { setSrcKey(s.key); setHover(null); setMenuOpen(false) }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: on ? 'hsl(var(--studio-surface-2))' : 'transparent', color: on ? 'hsl(var(--studio-text))' : 'hsl(var(--studio-text-muted))', fontFamily: 'var(--studio-font-sans)', fontSize: 12.5 }}>{s.label}</button>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
          {/* In SECTORS mode there is no single number to headline — the chart
              is a comparison of nine series, each with its own % in the legend.
              Showing the index figure there contradicted the chart under it. */}
          {mode === 'sectors' ? (
            /* "9 sectors" and "Memory vs Semi Equip" are very different widths
               at 58px display type — step the size down by length so a named
               pair does not run off the panel. */
            <span style={{ display: 'block', marginTop: 6, fontFamily: 'var(--studio-font-display)', fontSize: sectorHead.length > 24 ? 28 : sectorHead.length > 14 ? 38 : 58, lineHeight: 0.94, color: 'hsl(var(--studio-text))' }}>
              {sectorHead}
              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 11, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))', marginLeft: 12, verticalAlign: 'middle' }}>
                {sectorTf}{sectorLog ? ' · LOG' : ''}
              </span>
            </span>
          ) : (
            <Pct value={shownPct} animate={!scrub} style={{ display: 'block', marginTop: 6, fontFamily: 'var(--studio-font-display)', fontSize: 58, lineHeight: 0.94, color: `hsl(var(${numColor}))` }} />
          )}
          <div className="v3web-tfrow">
            {mode === 'index'
              ? PULSE_TFS.map(([l]) => <Chip key={l} active={tf === l} onClick={() => setTf(l)}>{l}</Chip>)
              : SECTOR_TFS.map(([l]) => <Chip key={l} active={sectorTf === l} onClick={() => setSectorTf(l)}>{l}</Chip>)}
            <button
              onClick={() => setMode(mode === 'sectors' ? 'index' : 'sectors')}
              aria-pressed={mode === 'sectors'}
              style={{ all: 'unset', cursor: 'pointer', marginLeft: 8, padding: '3px 9px', borderRadius: 999,
                border: `1px solid hsl(var(--studio-${mode === 'sectors' ? 'accent' : 'border'}))`,
                fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.1em',
                color: `hsl(var(--studio-${mode === 'sectors' ? 'accent' : 'text-subtle'}))` }}>
              SECTORS
            </button>
          </div>
        </div>
        <div className="v3web-movers">
          {movers.length === 0 && [0, 1, 2, 3].map((i) => (
            <div key={i} className="v3web-mv" style={{ cursor: 'default', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <SkelText w={58} h={12} />
              <SkelText w={64} h={11} />
            </div>
          ))}
          {movers.slice(0, 4).map((m, i) => {
            const up = m.c >= 0
            return (
              <div key={m.t} className="v3web-mv" onClick={() => onOpenTicker(m.t)} style={enterStyle(i)}>
                <div className="t"><StockLogo ticker={m.t} size={16} />{m.t}</div>
                <div className="c" style={{ color: `hsl(var(${intent(up)}))` }}>{up ? '▲' : '▼'} {Math.abs(m.c).toFixed(2)}%</div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="v3web-herostage">
        {mode === 'sectors' ? (
          <Crossfade loading={sectorSeries.length === 0 && sectorLoading} skeleton={<SkelChart height={224} />}>
            <SectorLines series={sectorSeries} height={224} days={sectorDays} log={sectorLog}
              selected={sectorSel} onToggle={toggleSel} onReset={resetSel} />
          </Crossfade>
        ) : (
          <Crossfade loading={points.length === 0 && chartLoading2} skeleton={<SkelChart height={224} />}>
            <HeroChart points={points} color={lineColor} height={224} intraday={intraday} onHover={setHover} pulse={!!livePulse} />
          </Crossfade>
        )}
      </div>
    </section>
  )
}

/* Merge signals + news into a compact dispatch list (Overview teaser).
   Signals ranked by conviction (0-100 confidence) desc, mirroring mobile's
   loud-first ordering. `ts` = raw ISO (row clock), `time` = relative ("3h ago")
   for the NewsSheet reader footer. */
function buildWire(events, news) {
  const sigs = (events || [])
    .filter((e) => e.signal_type !== 'news_sentiment_shift')
    .map((e) => {
      const dir = String(e.direction || '').toLowerCase()
      const up = dir === 'up' || dir === 'bullish' || dir === 'positive'
      const down = dir === 'down' || dir === 'bearish' || dir === 'negative'
      const conv = typeof e.confidence === 'number' ? (e.confidence > 1 ? e.confidence / 100 : e.confidence) : 0
      // api/signal-events selects the column `detail` (singular) — tolerate both
      const d = e.details || e.detail || {}
      return {
        kind: 'signal', id: `s-${e.id}`, t: e.ticker, type: e.signal_type,
        sentiment: up ? 'bullish' : down ? 'bearish' : null, conv,
        headline: signalWriteup(e), why: d.why || d.summary || '',
        ts: e.signal_date, time: relTime(e.signal_date), summary: d.summary || null,
      }
    })
    .sort((a, b) => b.conv - a.conv || new Date(b.ts || 0) - new Date(a.ts || 0))
    .slice(0, 12)
  const seen = new Set()
  const arts = (news || [])
    .filter((a) => { if (!a.url || seen.has(a.url)) return false; seen.add(a.url); return true })
    .slice(0, 8)
    .map((a) => ({
      kind: 'news', id: `n-${a.id}`, t: a.ticker, type: 'news', sentiment: null,
      headline: a.headline, why: a.source || 'News', ts: a.published_at, time: relTime(a.published_at),
      summary: a.summary, image: a.image_url, url: a.url,
    }))
  // Interleave, signals first, capped for the teaser.
  const out = []
  const max = Math.max(sigs.length, arts.length)
  for (let i = 0; i < max && out.length < 6; i++) {
    if (sigs[i]) out.push(sigs[i])
    if (arts[i] && out.length < 6) out.push(arts[i])
  }
  return out
}

function fmtTime(ts) {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'
  } catch { return '' }
}

function WireTeaser({ events, news, loading, onOpenTicker, onOpenNews }) {
  const items = useMemo(() => buildWire(events, news), [events, news])
  if (items.length === 0 && !loading) return null
  return (
    <section className="v3web-module">
      <div className="v3web-sechead"><span className="v3web-kicker">On the Wire</span></div>
      <Crossfade loading={items.length === 0 && loading} skeleton={
        <div>{[0, 1, 2, 3].map((i) => <SkelDisp key={i} i={i} />)}</div>
      }>
      <div>
        {items.map((it, ei) => {
          const up = it.sentiment === 'bullish'
          const down = it.sentiment === 'bearish'
          const col = up ? '--studio-success' : down ? '--studio-danger' : '--studio-text-subtle'
          return (
            <div key={it.id} className="v3web-disp" onClick={() => onOpenNews(signalToSheet(it))} style={enterStyle(ei)}>
              <div style={{ display: 'flex', gap: 10 }}>
                <span className="v3web-pip" style={{ background: `hsl(var(${col}))` }} />
                <span className="time">{fmtTime(it.ts)}</span>
              </div>
              <div className="v3web-disp-main">
                <div className="v3web-disp-meta" onClick={(e) => { e.stopPropagation(); onOpenTicker(it.t) }} style={{ cursor: 'pointer' }}>
                  <StockLogo ticker={it.t} size={20} />
                  <span className="tk">{it.t}</span>
                  <span className="src">{it.why}</span>
                </div>
                <h3 className="v3web-disp-head">{it.headline}</h3>
                {it.summary && <p className="v3web-disp-why">{it.summary}</p>}
              </div>
              {it.sentiment && (
                <div className="v3web-disp-sent">
                  <span className="arr" style={{ color: `hsl(var(${col}))` }}>{up ? '↑' : '↓'}</span>
                  <span className="lab" style={{ color: `hsl(var(${col}))` }}>{it.sentiment.toUpperCase()}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
      </Crossfade>
    </section>
  )
}

function PickCard({ pick, stats, history, onOpen }) {
  // Conviction is stored 0-1 OR 0-100 depending on the writer run — normalize
  // to the 0-100 display scale (mirrors mobile PickOfWeek).
  const conv = pick.conviction != null ? Math.round(pick.conviction * (pick.conviction <= 1 ? 100 : 1)) : null
  const thesis = pick.thesis || pick.headline || pick.analysis_md?.split('\n').find((l) => l.trim()) || ''
  const sub = pick.subhead || pick.catalyst || null
  const receipts = Array.isArray(history) ? history.length : 0
  return (
    <>
      <div className="v3web-sechead" style={{ marginTop: 6 }}><span className="v3web-kicker">Pick of the Week</span></div>
      <div className="v3web-pick" onClick={onOpen}>
        <div className="v3web-pick-eyebrow"><span className="d" /><span className="l">PICK OF THE WEEK · LIVE</span></div>
        <div className="v3web-pick-hd">
          <StockLogo ticker={pick.ticker} size={34} />
          <div>
            <div className="nm">{pick.ticker}</div>
            <div className="sub">{pick.company || pick.name || ''}</div>
          </div>
          <div className="right">
            {(pick.current_price ?? pick.locked_price) != null && <div className="px">${Number(pick.current_price ?? pick.locked_price).toFixed(2)}</div>}
            {conv != null && <div className="conv">CONV {conv}</div>}
          </div>
        </div>
        {thesis && <p className="v3web-pick-thesis">{thesis.length > 220 ? thesis.slice(0, 217) + '…' : thesis}</p>}
        {sub && <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.04em', color: 'hsl(var(--studio-text))', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 5 }}><span>{sub}</span><span style={{ opacity: 0.55 }}>→</span></div>}
        <div className="v3web-pick-foot">
          {/* api/pick-of-week emits { count, avg_pct, hit_rate } — no wins/losses */}
          {stats && stats.count > 0 && (
            <span className="sub">
              {`${stats.count} PICKS · ${stats.hit_rate != null ? Math.round(stats.hit_rate * 100) + '% HIT' : '—'} · avg ${stats.avg_pct != null ? (stats.avg_pct >= 0 ? '+' : '') + Number(stats.avg_pct).toFixed(1) + '%' : '—'}`}
            </span>
          )}
          {receipts > 0 && <span className="receipts" style={stats ? undefined : { marginLeft: 'auto' }}>RECEIPTS →</span>}
        </div>
      </div>
    </>
  )
}
